import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DataList } from "@/common/DataList";
import type { ActivitiesTabFormValues } from "@/entities/activities-tab-content/model";
import { ProfessionalActivitiesField } from "./ProfessionalActivitiesField";

type Props = {
  personId: string;
  isEditMode: boolean;
  handleEditMode: () => void;
  onSubmit: (data: ActivitiesTabFormValues) => void;
  disabled?: boolean;
};

export const ProfessionalActivitiesList = ({
  personId,
  isEditMode,
  handleEditMode,
  onSubmit,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const { control, getValues, trigger } = useFormContext<ActivitiesTabFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "professionalActivities",
  });

  const onChange = () => {
    trigger("professionalActivities");
  };

  const onBlur = async () => {
    const isValid = await trigger("professionalActivities");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  const addHandler = () => {
    if (!isEditMode) {
      handleEditMode();
    }

    setTimeout(() => {
      append({
        id: undefined,
        activity: "",
        personId: personId,
        role: "",
        industry: { name: "" },
      });
    }, 0);
  };

  const removeHandler = async (index: number) => {
    remove(index);

    const isValid = await trigger("professionalActivities");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  return (
    <DataList
      data={fields.map((item) => ({
        id: item?.id ?? "",
        label: item?.activity ?? "",
        subLabel: item?.industry?.name ?? "",
        value: item?.role ?? "",
      }))}
      onAddClick={addHandler}
      onTrashClick={removeHandler}
      isEdit={isEditMode}
      title={t("contactsPage.activities.professionalActivities", { count: 2 })}
      editNode={(index) => (
        <ProfessionalActivitiesField index={index} onBlur={onBlur} onChange={onChange} />
      )}
      disabled={disabled}
      hint={isEditMode ? t("contactsPage.editProfessionalActivitiesHint") : undefined}
    />
  );
};
